/* eslint-disable max-len */
import { useEffect, useMemo, useState } from "react";
import {
  useTranslate,
  useUpdate,
  useDeleteMany,
  useGetOne,
  useGetList,
  useDataProvider,
  useNotify
} from "react-admin";
import { useParams, useNavigate } from "react-router-dom";
import * as yup from "yup";

import * as User from "providers/user";
import CustomHeaderActions from "./CustomHeaderActions";
import { useQuery } from "@apollo/client";
import { GET_SORTED_MENU } from "gql";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  IconButton,
  TextField
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SortableList } from "./SortableList";
import { muiColors } from "themes/colors";
import { CustomButton } from "./CustomButton";
import { isEqual } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import MenuDndSectionItem from "./MenuDndSectionItem";

export default function MenuEdit() {
  const { permissions } = User.useUser();
  const [deleteMany] = useDeleteMany();
  const translate = useTranslate();
  const [edit] = useUpdate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const params = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [items, setItems] = useState<any>([]);
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const [name, setName] = useState<string>("");
  const [originalData, setOriginalData] = useState({
    name: "",
    menu_sections: [] as any[]
  });

  const { data: currentRecord } = useGetOne("Menu", { id: params.id });
  const { data: menuData } = useQuery(GET_SORTED_MENU, {
    variables: {
      where: {
        id: params.id
      },
      orderBy: [
        {
          section_position: "asc"
        }
      ],
      menuSectionServicesOrderBy2: [
        {
          section_position: "asc"
        }
      ]
    },
    skip: !params.id,
    fetchPolicy: "no-cache"
  });
  const { data: allServices } = useGetList("Service", {
    filter: {
      company_id: permissions?.companyId
    },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 500 }
  });

  const serviceChoices = allServices
    ?.filter((item) => !item.is_addon)
    ?.map((item) => {
      return {
        id: item?.id,
        name: item.name,
        price: item?.price,
        description: item?.description
      };
    });

  useEffect(() => {
    if (menuData?.menu?.id) {
      setItems(menuData?.menu?.menu_sections);
      setName(menuData?.menu?.name);
      setOriginalData({
        name: menuData.menu.name,
        menu_sections: menuData.menu.menu_sections
      });
    }
  }, [menuData]);

  const isDirty = useMemo(() => {
    if (originalData.name !== name) return true;
    if (!isEqual(originalData.menu_sections, items)) return true;
    return false;
  }, [name, items, originalData]);

  const isDndHidden = expandedAccordions?.size > 0;

  const handleAccordionChange = (itemId: string, expanded: boolean) => {
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      if (expanded) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const removeSection = (itemId: string) => {
    setItems((prev: any) => prev.filter((i: any) => i?.id !== itemId));
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const submit = async () => {
    setIsLoading(true);

    const formData = {
      name,
      menu_sections: items.map((section: any) => ({
        name: section.name,
        services: (section.menu_section_services || []).map((service: any) =>
          service.id ? service.id : service
        )
      }))
    };

    try {
      await validationSchema.validate(formData, { abortEarly: false });
    } catch (err: any) {
      if (err.inner && err.inner.length) {
        const errorMessages = err.inner
          .map((e: any) => {
            const path = e.path;
            const match = path.match(/menu_sections\[(\d+)\]/);
            if (match) {
              const sectionNumber = parseInt(match[1], 10) + 1;
              return `Section ${sectionNumber}: ${e.message}`;
            }
            return e.message;
          })
          .join("\n");

        notify(
          <Alert
            severity="error"
            sx={{
              whiteSpace: "pre-line",
              bgcolor: muiColors.error,
              color: "#fff !important"
            }}
          >
            {errorMessages}
          </Alert>,
          { type: "warning", autoHideDuration: 7000 }
        );
      } else {
        notify(`${err.message}`, { type: "warning", autoHideDuration: 7000 });
      }
      setIsLoading(false);
      return;
    }

    try {
      if (
        currentRecord?.menu_sections &&
        currentRecord.menu_sections.length > 0
      ) {
        const sectionIds = currentRecord.menu_sections
          .filter((section: any) => section.id)
          .map((section: any) => section.id);
        if (sectionIds.length > 0) {
          await deleteMany("MenuSection", { ids: sectionIds });
        }
      }

      for (let index = 0; index < items.length; index++) {
        const section = items[index];
        const newSectionData = {
          name: section.name,
          company: { id: permissions.companyId },
          menu: { id: params.id },
          section_position: index + 1,
          menu_section_services: (section.menu_section_services || []).map(
            (serviceItem: any, idx: number) => ({
              service: {
                id: serviceItem?.service?.id
                  ? serviceItem?.service?.id
                  : serviceItem.id
                  ? serviceItem.id
                  : serviceItem
              },
              section_position: idx + 1
            })
          )
        };
        await dataProvider.create("MenuSection", { data: newSectionData });
      }

      await edit(
        "Menu",
        {
          id: params.id,
          data: {
            name,
            company: { id: permissions.companyId }
          }
        },
        {
          onSuccess: () => {
            setIsLoading(false);
            navigate("/dashboard/Menu");
          },
          onError: () => {
            setIsLoading(false);
            notify("Menu update failed", { type: "warning" });
          }
        }
      );
    } catch (error) {
      notify("There was an error updating the menu", { type: "error" });
      setIsLoading(false);
    }
  };

  return (
    <Box
      className="menu-dnd-container"
      sx={{
        "& .MuiAccordion-root": {
          boxShadow: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.04) !important"
        },
        "& .MuiAccordionSummary-root": {
          minHeight: "36px !important",
          paddingLeft: "0px !important"
        },
        "& .MuiAccordionDetails-root": {
          padding: "8px 16px 8px 30px !important"
        },
        "&.Mui-expanded": {
          minHeight: "36px !important",
          margin: "0px !important"
        },
        "& .MuiAccordionSummary-content": {
          margin: "0px !important"
        }
      }}
    >
      <CustomHeaderActions title={translate("pagenames.menu")} />
      <TextField
        variant="filled"
        label={translate("common.name")}
        data-cy="description-input"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
        fullWidth
        sx={{
          marginBottom: "16px !important",
          "& .MuiFilledInput-input": {
            paddingTop: "22px",
            paddingBottom: "5px",
            fontWeight: 400
          },
          "& .MuiFormHelperText-root": {
            display: "none"
          }
        }}
      />
      <div className="text-[14px] h-[25px] text-[rgba(48,47,46,0.6)]">
        {translate("services.menu_sections")}:
      </div>
      <SortableList
        items={items}
        onChange={setItems}
        renderItem={(item: any, itemIndex: number | undefined) => {
          return (
            <SortableList.Item id={item?.id}>
              <Accordion
                key={item?.id}
                sx={{ width: "100%" }}
                onChange={(_, expanded) =>
                  handleAccordionChange(item.id, expanded)
                }
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  sx={{
                    "&.Mui-expanded": {
                      minHeight: "36px !important",
                      margin: "0px !important"
                    }
                  }}
                >
                  {!isDndHidden ? (
                    <SortableList.DragHandle />
                  ) : (
                    <div className="w-[31px] h-[42px]" />
                  )}
                  <div className="w-full text-[16px] font-[600] flex items-center">
                    {/* Section: {item.name} */}
                    <div className="min-w-[24px] ml-2">
                      {itemIndex !== undefined && itemIndex + 1 + ". "}
                    </div>
                    <input
                      className="border-none max-w-[320px] w-full outline-none bg-transparent text-[16px] font-[500] focus:!border-mono-primary focus:!shadow-none focus:ring-mono-primary"
                      onClick={(e) => e.stopPropagation()}
                      placeholder={translate("menusections.section_name")}
                      value={item?.name || ""}
                      onChange={(e) => {
                        setItems((prev: any) =>
                          prev.map((i: any) =>
                            i.id === item.id
                              ? { ...i, name: e.target.value }
                              : i
                          )
                        );
                      }}
                    />
                    <IconButton
                      sx={{
                        marginLeft: "16px",
                        width: "32px",
                        height: "32px"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSection(item.id);
                      }}
                    >
                      <CloseIcon
                        sx={{
                          width: "24px",
                          height: "24px",
                          color: muiColors.error
                        }}
                      />
                    </IconButton>
                  </div>
                </AccordionSummary>
                <AccordionDetails className="menu-sections-container">
                  <MenuDndSectionItem
                    menu_section={item}
                    serviceChoices={serviceChoices}
                    onChangeServices={(
                      newServices: any,
                      menuSectionId: string
                    ) => {
                      setItems((prev: any) =>
                        prev.map((i: any) =>
                          i.id === menuSectionId
                            ? { ...i, menu_section_services: newServices }
                            : i
                        )
                      );
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </SortableList.Item>
          );
        }}
      />
      <CustomButton
        text={translate("menusections.add_menu_section")}
        variant="secondary"
        onClick={() => {
          setItems((prev: any) => [
            ...prev,
            {
              id: Math.random().toString(),
              name: "",
              menu_section_services: []
            }
          ]);
        }}
        sx={{ marginTop: "16px", borderColor: "#B8722A", color: "#B8722A" }}
      />
      <CustomButton
        text={translate("common.save")}
        variant="primary"
        disabled={!isDirty || isLoading}
        onClick={() => {
          submit();
        }}
        sx={{ marginTop: "32px" }}
      />
    </Box>
  );
}

const validationSchema = yup
  .object()
  .shape({
    name: yup.string().required("Menu name required."),
    menu_sections: yup
      .array()
      .min(1, "At least one section is required.")
      .of(
        yup.object().shape({
          name: yup.string().required("Section name is required."),
          services: yup
            .array()
            .min(1, "At least one service is required.")
            .of(yup.string())
            .required("At least one service is required.")
        })
      )
      .required("At least one section is required.")
  })
  .required();
